import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Text,
  tokens
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { UsageStats } from '../types';
import { apiService } from '../services/api.service';
import '../styles/components/subscription-card.css';

interface SubscriptionCardProps {
  onLicenseActivated?: () => void;
}

export const SubscriptionCard: React.FC<SubscriptionCardProps> = ({ onLicenseActivated }) => {
   const { t } = useTranslation('common');
   const [stats, setStats] = useState<UsageStats | null>(null);
   const [loading, setLoading] = useState(true);
   const [licenseKey, setLicenseKey] = useState('');
   const [activating, setActivating] = useState(false);
   const [error, setError] = useState<string | null>(null);
   const [success, setSuccess] = useState(false);
   const [currentLicenseKey, setCurrentLicenseKey] = useState<string | null>(null);
   const [tenantData, setTenantData] = useState<any>(null);
   const [unsubscribing, setUnsubscribing] = useState(false);
   const [unsubscribeSuccess, setUnsubscribeSuccess] = useState(false);
   const [showUnsubscribeModal, setShowUnsubscribeModal] = useState(false);

  useEffect(() => {
    loadUsageStats();
    loadTenantData();
  }, []);

  // ✨ Auto-refresh when subscription status changes (e.g., after Unsubscribe webhook)
  useEffect(() => {
    if (!tenantData) return;

    // Set up polling to refresh tenant data every 5 seconds
    // This ensures the UI updates when the webhook changes subscription status
    const pollInterval = setInterval(() => {
      loadTenantData();
    }, 5000);

    return () => clearInterval(pollInterval);
  }, [tenantData?.subscriptionStatus]);

  const loadUsageStats = async () => {
    try {
      setLoading(true);
      const response = await (apiService as any).getUsageStats();
      if (response.success && response.data) {
        setStats(response.data);
      }
    } catch (err) {
      console.error('Failed to load usage stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadTenantData = async () => {
    try {
      const response = await (apiService as any).getCurrentTenantInfo();
      if (response.success && response.data) {
        setTenantData(response.data);
        // ✨ BYOL → Marketplace: Only show BYOL license if no active marketplace subscription
        // AND subscription is not cancelled
        if (
          response.data.licenseKey &&
          !response.data.subscriptionId &&
          response.data.subscriptionStatus !== 'cancelled'
        ) {
          setCurrentLicenseKey(response.data.licenseKey);
        } else {
          // Clear BYOL license if marketplace subscription exists or is cancelled
          setCurrentLicenseKey(null);
        }
      }
    } catch (err) {
      console.error('Failed to load tenant data:', err);
    }
  };

   const handleActivateLicense = async () => {
     if (!licenseKey.trim()) {
       setError(t('subscription.licenseActivation.errorEmpty'));
       return;
     }

     setActivating(true);
     setError(null);

     try {
       const response = await (apiService as any).activateLicense(licenseKey);
       if (response.success) {
         setSuccess(true);
         setLicenseKey('');
         
         // Reload stats after successful activation
         setTimeout(() => {
           loadUsageStats();
           if (onLicenseActivated) {
             onLicenseActivated();
           }
         }, 1500);
       } else {
         setError(response.error || t('subscription.licenseActivation.errorInvalid'));
       }
     } catch (err) {
       setError(t('subscription.licenseActivation.errorActivation'));
     } finally {
       setActivating(false);
     }
   };

   const handleUnsubscribe = async () => {
     if (!tenantData?.subscriptionId) {
       setError(t('subscription.unsubscribeModal.errorNoSubscription'));
       return;
     }

     setUnsubscribing(true);
     setError(null);

     try {
       // ✅ SECURITY: No subscription ID passed - retrieved server-side from tenants table
       const response = await (apiService as any).unsubscribeFromMarketplace();
       if (response.success) {
         setUnsubscribeSuccess(true);
         setShowUnsubscribeModal(false);
         
         // Reload stats after successful unsubscribe
         setTimeout(() => {
           loadUsageStats();
           loadTenantData();
         }, 1500);
       } else {
         setError(response.error || t('subscription.unsubscribeModal.errorUnsubscribe'));
       }
     } catch (err) {
       setError(t('subscription.unsubscribeModal.errorUnsubscribe'));
       console.error('Unsubscribe error:', err);
     } finally {
       setUnsubscribing(false);
     }
   };

  const handleMarketplaceClick = async () => {
    try {
      const response = await (apiService as any).getMarketplaceUrl();
      if (response.success && response.data?.url) {
        window.open(response.data.url, '_blank');
      }
    } catch (err) {
      console.error('Failed to get marketplace URL:', err);
    }
  };

  const handleWebsiteClick = () => {
    window.open('https://shawork.com', '_blank');
  };

   const formatDate = (dateStr: string) => {
     const date = new Date(dateStr);
     const locale = t('_locale', { defaultValue: 'en-US' });
     return date.toLocaleDateString(locale, { 
       day: 'numeric', 
       month: 'long', 
       year: 'numeric' 
     });
   };

   if (loading) {
     return (
       <div className="subscription-card">
         <div className="subscription-card-header">
           <h2>{t('subscription.title')}</h2>
         </div>
         <div className="subscription-loading">
           <div className="spinner-small"></div>
           <p>{t('subscription.loading')}</p>
         </div>
       </div>
     );
   }

  if (!stats) {
    return null;
  }

  const isUnlimited = stats.currentMonth.limit === -1;
  const isNearLimit = stats.currentMonth.percentage >= 80;
  const isAtLimit = stats.currentMonth.percentage >= 100;
  const isPremium = stats.plan !== 'free';

  // Check if subscription is expired
  const isExpired = tenantData?.subscriptionStatus === 'expired' ||
    (stats.validUntil && new Date(stats.validUntil) < new Date());

   return (
     <div className="subscription-card">
       <div className="subscription-card-header">
         <h2>{t('subscription.title')}</h2>
       </div>

       {success && (
         <div className="subscription-success-banner">
           <span className="subscription-success-icon">✓</span>
           <span>{t('subscription.successBanner')}</span>
         </div>
       )}

       {tenantData?.subscriptionStatus === 'suspended' && (
         <div className="subscription-suspended-banner">
           <span className="subscription-suspended-icon">⚠️</span>
           <div className="subscription-suspended-content">
             <strong>{t('subscription.suspendedBanner.title')}</strong>
             <p>{t('subscription.suspendedBanner.message')}</p>
           </div>
         </div>
       )}

       {isExpired && (
         <div className="subscription-expired-banner">
           <span className="subscription-expired-icon">⏰</span>
           <div className="subscription-expired-content">
             <strong>{t('subscription.expiredBanner.title')}</strong>
             <p>{t('subscription.expiredBanner.message')}</p>
           </div>
         </div>
       )}

       <div className="subscription-card-content">
         {/* Left Section: Plan Status */}
         <div className="subscription-plan-section">
           <div className="subscription-plan-header">
             <h3>{t('subscription.currentPlan')}</h3>
             <div className="subscription-plan-badges">
               <span className={`subscription-badge subscription-badge--${stats.plan}`}>
                 {t(`subscription.planBadges.${stats.plan}`)}
               </span>
               {tenantData?.subscriptionStatus === 'suspended' && (
                 <span className="subscription-badge subscription-badge--suspended">
                   {t('subscription.planBadges.suspended')}
                 </span>
               )}
             </div>
           </div>

           {/* Display License Key and Expiration - BYOL only */}
           {currentLicenseKey && !tenantData?.subscriptionId && tenantData?.subscriptionStatus !== 'cancelled' && (
             <div className="subscription-license-info">
               <div className="subscription-license-key">
                 <label>{t('subscription.licenseInfo.byolLabel')}</label>
                 <code>{currentLicenseKey}</code>
               </div>
               {stats.validUntil && (
                 <div className="subscription-expiration-info">
                   <label>{t('subscription.licenseInfo.validUntil')}</label>
                   <span>{formatDate(stats.validUntil)}</span>
                 </div>
               )}
             </div>
           )}

           {/* Display Marketplace Subscription Info - Only if active (not cancelled) */}
           {tenantData?.subscriptionId && tenantData?.subscriptionStatus !== 'cancelled' && (
             <div className="subscription-marketplace-info">
               <div className="subscription-marketplace-id">
                 <label>{t('subscription.licenseInfo.marketplaceLabel')}</label>
                 <code>{tenantData.subscriptionId}</code>
               </div>
               {stats.validUntil && (
                 <div className="subscription-expiration-info">
                   <label>{t('subscription.licenseInfo.validUntil')}</label>
                   <span>{formatDate(stats.validUntil)}</span>
                 </div>
               )}
             </div>
           )}

           {!isUnlimited ? (
             <div className="subscription-usage-info">
               <div className="subscription-usage-text">
                 <strong>{stats.currentMonth.used} / {stats.currentMonth.limit} {t('subscription.usageInfo.requests')}</strong>
                 <span className="subscription-usage-reset">
                   {t('subscription.usageInfo.renewalDate', { date: formatDate(stats.resetDate) })}
                 </span>
               </div>

               <div className="subscription-progress-container">
                 <div className="subscription-progress-bar">
                   <div
                     className={`subscription-progress-fill ${
                       isAtLimit ? 'at-limit' : isNearLimit ? 'near-limit' : ''
                     }`}
                     style={{
                       width: `${Math.min(stats.currentMonth.percentage, 100)}%`
                     }}
                   />
                 </div>
                 <span className="subscription-progress-percentage">
                   {stats.currentMonth.percentage}%
                 </span>
               </div>

               {isAtLimit && (
                 <div className="subscription-warning">
                   {t('subscription.usageInfo.limitReached')}
                 </div>
               )}
             </div>
           ) : !isExpired && tenantData?.subscriptionStatus !== 'suspended' ? (
             <div className="subscription-unlimited">
               <div className="subscription-unlimited-badge">{t('subscription.unlimited.badge')}</div>
               <p className="subscription-unlimited-text">
                 {t('subscription.unlimited.text')}
               </p>
               {stats.validUntil && (
                 <p className="subscription-expiration">
                   {t('subscription.unlimited.validUntil', { date: formatDate(stats.validUntil) })}
                 </p>
               )}
             </div>
           ) : (
             <div className="subscription-downgrade-action">
               <div className="subscription-downgrade-icon">{t('subscription.downgrade.icon')}</div>
               <div className="subscription-downgrade-content">
                 <h4>{t('subscription.downgrade.title')}</h4>
                 <p>{t('subscription.downgrade.message', { status: isExpired ? t('subscription.downgrade.messageExpired') : t('subscription.downgrade.messageSuspended') })}</p>
               </div>
               <button
                 onClick={() => setShowUnsubscribeModal(true)}
                 disabled={unsubscribing}
                 className="subscription-downgrade-btn"
                 title={t('subscription.downgrade.button')}
               >
                 {unsubscribing ? (
                   <>
                     <span className="spinner-tiny"></span>
                     <span>{t('subscription.unsubscribeModal.processingButton')}</span>
                   </>
                 ) : (
                   t('subscription.downgrade.button')
                 )}
               </button>
             </div>
           )}
        </div>

         {/* Right Section: License Activation */}
         <div className="subscription-license-section">
           <h3>{t('subscription.licenseActivation.title')}</h3>

           <div className="subscription-input-group">
             <input
               type="text"
               placeholder={t('subscription.licenseActivation.placeholder')}
               value={licenseKey}
               onChange={(e) => {
                 setLicenseKey(e.target.value);
                 setError(null);
               }}
               disabled={activating || success}
               className="subscription-input"
               aria-label={t('subscription.licenseActivation.inputLabel')}
             />
             <button
               onClick={handleActivateLicense}
               disabled={activating || success || !licenseKey.trim()}
               className="subscription-activate-btn"
               aria-label={t('subscription.licenseActivation.activateButton')}
             >
               {activating ? (
                 <>
                   <span className="spinner-tiny"></span>
                   <span>{t('subscription.licenseActivation.activatingButton')}</span>
                 </>
               ) : (
                 t('subscription.licenseActivation.activateButton')
               )}
             </button>
           </div>

           {error && (
             <div className="subscription-error">
               {error}
             </div>
           )}

           <div className="subscription-divider">
             <span>{t('subscription.obtainLicense.divider')}</span>
           </div>

           <div className="subscription-links">
             <button
               onClick={handleMarketplaceClick}
               className="subscription-link-item"
               title={t('subscription.obtainLicense.microsoftStore.tooltip')}
             >
               <span className="subscription-link-icon">🏪</span>
               <div className="subscription-link-content">
                 <div className="subscription-link-title">{t('subscription.obtainLicense.microsoftStore.title')}</div>
                 <div className="subscription-link-desc">{t('subscription.obtainLicense.microsoftStore.description')}</div>
               </div>
               <span className="subscription-link-arrow">→</span>
             </button>

             <button
               onClick={handleWebsiteClick}
               className="subscription-link-item"
               title={t('subscription.obtainLicense.shaworkWebsite.tooltip')}
             >
               <span className="subscription-link-icon">🌐</span>
               <div className="subscription-link-content">
                 <div className="subscription-link-title">{t('subscription.obtainLicense.shaworkWebsite.title')}</div>
                 <div className="subscription-link-desc">{t('subscription.obtainLicense.shaworkWebsite.description')}</div>
               </div>
               <span className="subscription-link-arrow">→</span>
             </button>
           </div>
         </div>
      </div>

       {/* Unsubscribe Confirmation Modal */}
       <Dialog open={showUnsubscribeModal} onOpenChange={(_, data) => !data.open && setShowUnsubscribeModal(false)}>
         <DialogSurface style={{ maxWidth: '500px' }}>
           <DialogBody>
             <DialogTitle>
               <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                 <span>{t('subscription.unsubscribeModal.title')}</span>
                 <Button
                   appearance="subtle"
                   icon={<Dismiss24Regular />}
                   onClick={() => setShowUnsubscribeModal(false)}
                 />
               </div>
             </DialogTitle>
             <DialogContent>
               <div style={{ padding: '16px 0' }}>
                 <Text as="p" style={{ marginBottom: '16px' }}>
                   {t('subscription.unsubscribeModal.question')}
                 </Text>
                 <div style={{
                   backgroundColor: tokens.colorPaletteYellowBackground1,
                   padding: '12px',
                   borderRadius: '6px',
                   marginBottom: '16px'
                 }}>
                   <Text size={200} weight="semibold">
                     {t('subscription.unsubscribeModal.warning')}
                   </Text>
                   <Text as="p" size={200} style={{ marginTop: '8px' }}>
                     {t('subscription.unsubscribeModal.warningDetails')}
                   </Text>
                 </div>
               </div>
             </DialogContent>
             <DialogActions>
               <Button
                 appearance="secondary"
                 onClick={() => setShowUnsubscribeModal(false)}
               >
                 {t('subscription.unsubscribeModal.cancelButton')}
               </Button>
               <Button
                 appearance="primary"
                 onClick={handleUnsubscribe}
                 disabled={unsubscribing}
               >
                 {unsubscribing ? (
                   <>
                     <span style={{ marginRight: '8px' }}>⏳</span>
                     {t('subscription.unsubscribeModal.processingButton')}
                   </>
                 ) : (
                   t('subscription.unsubscribeModal.confirmButton')
                 )}
               </Button>
             </DialogActions>
           </DialogBody>
         </DialogSurface>
       </Dialog>
    </div>
  );
};
